import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DropzoneComponent } from 'ngx-dropzone-wrapper';
import { EntityFileUploadModel } from 'src/app/core/models/file/entity-file-upload-model';
import { AuthMyfileService } from 'src/app/core/services/auth.myfile.service';
import { MyfileApiService } from 'src/app/core/services/myfile-api.service';
import { ToasterNotificationService } from 'src/app/core/services/toaster-notification.service';
import { EntityTypeEnum } from "src/app/core/enums/entity-type-id-enum";
import { Subject } from 'rxjs';
import { AiApiService } from 'src/app/core/services/ai-api.service';
import { SecurityOperations } from 'src/app/core/security/security-operations';
import { Roles } from 'src/app/core/security/roles';
import { NgSelectComponent } from '@ng-select/ng-select';
import { GlobalComponent } from 'src/app/global-component';

@Component({
  selector: 'app-file-upload-modal',
  templateUrl: './file-upload-modal.component.html',
  styleUrls: ['./file-upload-modal.component.scss']
})
export class FileUploadModalComponent implements OnInit {
  @ViewChild(NgSelectComponent) ngSelectComponent: NgSelectComponent | undefined;
  @ViewChild(DropzoneComponent) componentRef: DropzoneComponent | undefined;
  dropzone: any;
  spinnerIsShown: boolean = false;
  submitDisabled: boolean = true;
  files: File[] = [];

  tags: string[] = [];
  canLoadToKnowledgeBase: boolean = true;

  minLengthTerm: number = 1;
  searchTerm: string | undefined;
  searchTerm$ = new Subject<string>();

  TagTermLength: number = 0;
  isPermittedTagListOpen: boolean = false;
  permittedTagList: string[] = [];
  defaultTagList: string[] = GlobalComponent.defaultTagList;
  canAddToKnowledgeBase: boolean = false;

  @Input() public entityFileUpload: EntityFileUploadModel = new EntityFileUploadModel(); // pass the model 
  @Input() public refreshList: any;
  @Input() public service: any;
  @Input() public givenMiniIndex?: number;
  @Input() public entityName?: string;

  @Output() entityFilestoAddListChanged = new EventEmitter<any>();
  @Output() minimize: EventEmitter<any> = new EventEmitter();

  constructor(
    private aiApiService: AiApiService,
    private authService: AuthMyfileService,
    private myFileApiService: MyfileApiService,
    private modalRef: NgbActiveModal,
    private toasterNotificationService: ToasterNotificationService
  ) {
    this.entityFileUpload.entityId = authService.currentUserContextValue?.id;
    this.entityFileUpload.entityTypeId = EntityTypeEnum.User;
  }

  ngAfterViewInit() {
    this.dropzone = this.componentRef?.directiveRef?.dropzone();
  }

  ngOnInit(): void {
    this.canAddToKnowledgeBase = this.authService.canAccessOperation(SecurityOperations.ai.loadFileToKnowledgeBase) || this.authService.isInRole(Roles.globalAdmin);
    this.entityName = `File Upload: ${this.entityName}`
    this.permittedTagList = this.defaultTagList;
    this.searchTerm$.subscribe(search => {
      if (search != undefined) {
        this.searchTerm = search;
      }
    });
  }

  onRemove(event: any) {
    if (this.dropzone.getAcceptedFiles().length > 0) {
      this.submitDisabled = false;
    } else {
      this.submitDisabled = true;
    }
  }

  /** 
   * Sends all queued files to our API (not the Data Entry API)
   * Triggered when the user clicks the 'Add' button
   * @returns {void}
   * */
  processQueue(): void {
    this.dropzone.processQueue();
  }

  /** 
   * Gets the xhr object and the formData objects as second and third parameters, so they can be modified or additional data can be added
   * Triggered by dropzone just before each file is sent
   * @param {any} args - The dropzone arguments
   * @returns {void} 
   * */
  sending(args: any[]): void {
    this.submitDisabled = true;
    var authModel = this.authService.getAuthFromLocalStorage();
    if (this.tags !== undefined && this.tags.length > 0) {
      args[2].append('Tags', new Blob([`"${this.tags.join()}"`], { type: 'text/plain'}),'');
    }
    args[1].setRequestHeader("Authorization", `Bearer ${authModel?.jwtAccessToken?.accessToken}`);
  }

  /** 
   * Displays an error messages if one or more files were rejected by dropzone
   * Triggered by dropzone when an upload was either successfull or erroneous
   * @param {any} args - The dropzone arguments
   * @returns {void}
   * */
  complete(args: any): void {
    if (this.dropzone.getAcceptedFiles().length > 0) {
      this.submitDisabled = false;
    } else {
      this.submitDisabled = true;
    }
  }

  /** 
   * For now, this just writes success messages to the console for debugging purposes
   * Triggered when a file has been uploaded successfully
   * @param {any} args - The dropzone arguments
   * @returns {void}
   * */
  uploadSuccess(args: any): void {
    this.toasterNotificationService.showSuccess('Files Successfully Staged. Click Add to Upload', this.entityName!)
    var stagingFileId = args[1];
    this.entityFileUpload.fileIdsToAdd.push(stagingFileId);
    this.files.push(args[0]);
  }

  /** 
 * For now, this just writes error messages tos the console for debugging purposes
 * Triggered when an upload error occurs
 * @param {any} args - The dropzone arguments
 * @returns {void}
 * */
  uploadError(args: any): void {
    if (args && args[1]) {
      this.dropzone.removeFile(args[0]);
      this.toasterNotificationService.showWarning(args[1], $localize`:@@Toast_Error_GeneralTitle:Error`);
    }
  }

  //i think i can rid of this call and just always return the array of IDS back to filemangement list component - but i am right now sure if this will cause any other issues so i am leaving this call here for now along with the service call.
  processEntityFiles(): void {
    this.spinnerIsShown = true;
    this.submitDisabled = true;
    this.addTagsToEachFile();
    this.myFileApiService.processEntityFiles(this.entityFileUpload).subscribe(
      (result: boolean) => {
        this.spinnerIsShown = false;
        this.submitDisabled = false;
        //call here to reload the parents list for now. 
        this.service.pageIndex = this.service.pageIndex;

        this.modalRef.close();
      }
    )
  }

  public returnFilestoAddList() {
    this.submitDisabled = true;
    this.entityFilestoAddListChanged.emit(this.entityFileUpload.fileIdsToAdd);
    this.modalRef.close();
    this.submitDisabled = false;
  }

  minimizeButton() {
    var modal = document.getElementById("openModal");
    if (modal) {
      this.minimize.emit({ index: this.givenMiniIndex, minimize: true, name: this.entityName });
    }
  }

  close() {
    this.modalRef.close();
  }
  dismiss() {
    this.modalRef.dismiss();
  }

  addTagsToEachFile() {
    if (this.tags && this.tags.length > 0) {

      this.entityFileUpload.fileIdsToAdd.forEach((result) => {

        // var ragEntityFileRequestModel = {
        //   fileName: rowModel.fileName,
        //   getFileList: {
        //     entityId: this.entityId,
        //     entityTypeId: this.entityTypeId
        //   },
        //   tags: rowModel.tags,
        //   fileId: rowModel.id
        // }

        // this.aiApiService.chatKnowledgeBaseLoad(ragEntityFileRequestModel).subscribe((result)=>{
        //   this.spinnerIsShown = false;
        //   this.close();
        // })
      })
    }
  }


  arrayCleared() {
    this.TagTermLength = 0;
    // this.settings.permittedTags = [];
  }

  accessTypeListController() {
    this.isPermittedTagListOpen = !this.isPermittedTagListOpen;
  }


  addTag(newTag: string) {
    if (newTag && newTag.length > 0) {
      this.TagTermLength = newTag.length;
      this.tags.push(`.${newTag}`)
    }
  }

  addCustomTag(tag: any) {
    return tag;
  }

  onBlurEvent(event: any) {
    if (event.target.value && event.target.value.length > 0) {
      this.TagTermLength = event.target.value.length;
      this.tags = [...this.tags, `${event.target.value}`];
      this.permittedTagList = [...this.defaultTagList, ...this.tags];
      this.searchTerm$.next('');
      this.ngSelectComponent!.searchTerm = '';
      this.isPermittedTagListOpen = false;
    }
  }
}
